import {
  COMBO_ANIM_DEFAULT_SPEED,
  COMBO_ANIM_DEFAULT_SPEED_VALUE,
  COMBO_ANIM_SPEEDS,
} from "@library/constants";
import { useEffect, type RefObject } from "react";
import {
  animateRkpi,
  getRkpiDataFromAttrs,
  getRkpiDurationFromSpeed,
} from "../utils";
import { IN_VIEW_THRESHOLDS, isTextInView } from "../utils/in-view";

const elementObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      // Not isIntersecting: Chrome reports false below the threshold, and a
      // text taller than the screen never reached 0.6 (see isTextInView).
      if (!isTextInView(entry)) return;
      handleTextNodesAnimation(entry.target as HTMLElement);
      elementObserver.unobserve(entry.target);
    });
  },
  { threshold: IN_VIEW_THRESHOLDS },
);

export const useTextAnimation = ({
  ref,
  disabled,
}: {
  ref: RefObject<HTMLDivElement>;
  // The PDF: its content is already in the final state (showFinalState),
  // which resetting highlights here would undo.
  disabled?: boolean;
}) => {
  useEffect(() => {
    if (!ref.current || disabled) return;

    const removeMutationOberver = initMutationObserver(ref.current);
    elementObserver.observe(ref.current);

    return () => {
      removeMutationOberver();
    };
  }, [ref, disabled]);
};

// === RKPI === //
const isRkpiActive = (el: unknown) => {
  if (!(el instanceof HTMLElement)) return false;
  return el.getAttribute("data-rkpi-active") === "true";
};
const setIsRkpiActive = (el: Element, value: boolean) => {
  el.setAttribute("data-rkpi-active", value ? "true" : "false");
};
const startRkpiAnimation = (item: Element) => {
  if (isRkpiActive(item)) return;

  setIsRkpiActive(item, true);
  const { to, from, prefix, suffix, speed, delay, decimal } =
    getRkpiDataFromAttrs(item);
  animateRkpi(
    item,
    from,
    to,
    getRkpiDurationFromSpeed(speed),
    delay,
    prefix,
    suffix,
    decimal,
  );
};

const observer = (
  element: HTMLElement,
  callback: (element: HTMLDivElement) => void,
) => {
  const elementObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        callback(entry.target as HTMLDivElement);
        elementObserver.unobserve(entry.target);
      });
    },
    { threshold: 0.6 },
  );
  elementObserver.observe(element);
};

const initMutationObserver = (node: HTMLDivElement) => {
  const throttledMutationObserver = (mutations: MutationRecord[]) => {
    mutations.forEach((mutation) => {
      if (mutation?.type !== "childList") return;

      mutation.addedNodes.forEach((n) => {
        if (n instanceof HTMLElement) {
          cleanTextNodeAttributes(node);
          elementObserver.observe(node);
        }
      });
    });
  };
  const comboMutationObserver = new MutationObserver(throttledMutationObserver);
  comboMutationObserver.observe(node, {
    attributes: true,
    childList: true,
    subtree: true,
  });

  return () => comboMutationObserver.disconnect();
};

// === HIGHLIGHTS === //
const isHighlightActive = (el: unknown) => {
  if (!(el instanceof HTMLElement)) return false;
  return el.getAttribute("data-highlight-active") === "true";
};
const setIsHighlightActive = (el: Element, value: boolean) => {
  el.setAttribute("data-highlight-active", value ? "true" : "false");
};
const startHighlightAnimation = (item: Element) => {
  if (isHighlightActive(item)) return;
  setIsHighlightActive(item, true);
};

// === TEXT ANIMATION === //
export const animateElements = (node: HTMLDivElement) => {
  const elements = Array.from(node.querySelectorAll("p, h1, h2, h3, li"));
  const delayName =
    node
      .closest('[data-layer-type="combo"]')
      ?.getAttribute("data-combo-animation-speed") ?? COMBO_ANIM_DEFAULT_SPEED;
  const delay =
    COMBO_ANIM_SPEEDS[delayName as keyof typeof COMBO_ANIM_SPEEDS] ??
    COMBO_ANIM_DEFAULT_SPEED_VALUE;

  elements.forEach((item, index) => {
    setTimeout(() => {
      item.setAttribute("data-is-animation-active", "true");
    }, index * delay);
  });
};

const cleanTextNodeAttributes = (node: HTMLElement) => {
  const elements = Array.from(node.querySelectorAll("p, h1, h2, h3, li"));
  elements.forEach((el) => {
    el.removeAttribute("data-is-animation-active");
  });

  const highlights = Array.from(
    node.querySelectorAll("[data-highlight-active]"),
  );
  highlights.forEach((el) => {
    el.setAttribute("data-highlight-active", "false");
  });
};

// === TEXT ANIMATION === //

const handleTextNodesAnimation = (node: HTMLElement) => {
  const rkpiElements = Array.from(node.querySelectorAll("[data-rkpi-active]"));
  const highlightElements = Array.from(
    node.querySelectorAll("[data-highlight-active]"),
  );
  rkpiElements.forEach((el) => observer(el as HTMLElement, startRkpiAnimation));
  highlightElements.forEach((el) =>
    observer(el as HTMLElement, startHighlightAnimation),
  );
  node.setAttribute("data-is-animation-active", "true");
};
